import type { Profile, UserOnboarding } from '../types/database.types';

export type StoredUserCache = Record<string, unknown>;

export function readUserCache(): StoredUserCache {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}') as StoredUserCache;
  } catch {
    return {};
  }
}

export function writeUserCache(patch: StoredUserCache): void {
  const existing = readUserCache();
  const merged: StoredUserCache = { ...existing };

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === null) continue;

    // Don't let empty Supabase arrays/strings wipe locally saved profile data
    if (Array.isArray(value) && value.length === 0) {
      const existingVal = existing[key];
      if (Array.isArray(existingVal) && existingVal.length > 0) continue;
    }
    if (typeof value === 'string' && value === '') {
      const existingVal = existing[key];
      if (typeof existingVal === 'string' && existingVal.trim() !== '') continue;
    }

    merged[key] = value;
  }

  localStorage.setItem('user', JSON.stringify(merged));
}

function parseLocationParts(location?: string | null): {
  city?: string;
  state?: string;
  country?: string;
} {
  if (!location?.trim()) return {};
  const parts = location.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return { city: parts[0], state: parts[1], country: parts.slice(2).join(', ') };
  }
  if (parts.length === 2) {
    return { city: parts[0], state: parts[1] };
  }
  if (parts.length === 1) {
    return { city: parts[0] };
  }
  return {};
}

/** Merge Supabase profile + onboarding into the local `user` cache for dashboard UI. */
export function buildUserCacheFromProfileAndOnboarding(
  profile: Profile,
  onboarding?: UserOnboarding | null
): StoredUserCache {
  const userType = profile.role === 'referrer' ? 'working_professional' : 'job_seeker';
  const locParts = parseLocationParts(profile.location);
  const p = profile as Profile & Record<string, unknown>;

  const companyEmail =
    onboarding?.company_email?.trim() || profile.office_email?.trim() || '';
  const companyName =
    onboarding?.company_name?.trim() || profile.company_name?.trim() || '';
  const contactNumber =
    onboarding?.contact_number?.trim() || profile.mobile_number?.trim() || '';
  const linkedinLink =
    onboarding?.linkedin_profile_link?.trim() || profile.linkedin_url?.trim() || '';
  const country = onboarding?.country?.trim() || locParts.country || '';
  const state = onboarding?.state?.trim() || locParts.state || '';
  const city = onboarding?.city?.trim() || locParts.city || '';
  const location =
    [city, state, country].filter(Boolean).join(', ') || profile.location?.trim() || '';

  const skills = Array.isArray(p.skills) ? (p.skills as string[]) : [];
  const preferredLocationList = Array.isArray(p.preferred_locations)
    ? (p.preferred_locations as string[])
    : [];
  const workModes = Array.isArray(p.work_modes) ? (p.work_modes as string[]) : [];
  const educationEntries = Array.isArray(p.education_entries) ? p.education_entries : [];
  const projectEntries = Array.isArray(p.project_entries) ? p.project_entries : [];
  const certificationEntries = Array.isArray(p.certification_entries) ? p.certification_entries : [];
  const workHistoryEntries = Array.isArray(p.work_history_entries) ? p.work_history_entries : [];
  const achievementEntries = Array.isArray(p.achievement_entries) ? p.achievement_entries : [];
  const hobbies = Array.isArray(p.hobbies) ? (p.hobbies as string[]) : [];
  const professionalEducation = p.professional_education ?? null;

  return {
    id: profile.id,
    full_name: profile.full_name,
    name: profile.full_name,
    primary_email: profile.primary_email,
    email: profile.primary_email,
    role: profile.role,
    userType,
    auth_provider: profile.auth_provider,
    profile_picture: profile.profile_picture,
    avatar: profile.profile_picture,
    designation: profile.designation,
    company_name: companyName,
    companyName,
    companyEmail,
    office_email: companyEmail || profile.office_email,
    officialEmail: companyEmail,
    mobile_number: contactNumber,
    contactNumber,
    location,
    country,
    state,
    city,
    bio: profile.bio,
    is_verified: profile.is_verified,
    email_verified: profile.email_verified,
    linkedinLink,
    linkedin_url: linkedinLink,
    linkedin_profile_link: linkedinLink,
    githubLink: (p.github_link as string)?.trim() || onboarding?.github_link?.trim() || undefined,
    github_link: (p.github_link as string)?.trim() || onboarding?.github_link?.trim() || undefined,
    portfolioUrl: (p.portfolio_url as string)?.trim() || undefined,
    skills,
    preferredLocationList,
    preferredLocations: preferredLocationList.join(', '),
    availabilityStatus: (p.availability_status as string) || '',
    noticePeriod: (p.notice_period as string) || '',
    workModes,
    industry: (p.industry as string) || '',
    languages: (p.languages as string) || '',
    yearsOfExperience: (p.years_of_experience as string) || '',
    educationEntries,
    projectEntries,
    certificationEntries,
    professionalEducation,
    workHistoryEntries,
    achievementEntries,
    hobbies,
    onboardingCompleted: Boolean(onboarding?.completed_at),
    user_code: p.user_code || (onboarding as any)?.user_code || undefined,
    userCode: p.user_code || (onboarding as any)?.user_code || undefined,
    // WP verification state — used by DashboardLayout to redirect pending users
    verification_status: (onboarding as any)?.verification_status ?? null,
    user_type_onboarding: (onboarding as any)?.user_type ?? null,
  };
}
